import { autobind } from 'core-decorators';
import React, { PureComponent } from 'react';
import { processing } from 'decorators';
import { fetchFeatureFlags, NormalizedFeatureFlags } from 'services/api/featureFlags';
import { withErrorNotification } from 'storage/NotificationsContext';
import mainWithContext from 'with/withContext';

type FeatureFlagsContextInterface = {
  flags: NormalizedFeatureFlags;
  isFetching: boolean;
  error?: Error;
  reload: () => Promise<void>;
  get: (flag: string, defaultValue?: boolean) => boolean;
  isEnabled: (flag: string) => boolean;
};

const defaultValue: FeatureFlagsContextInterface = {
  flags: {},
  isFetching: false,
  reload: () => Promise.resolve(),
  get: () => false,
  isEnabled: () => false,
};

const FeatureFlagsContext = React.createContext<FeatureFlagsContextInterface>(defaultValue);

export const CONNECT_AUTHENTICATION_FLAG = 'connect-authentication';
export const CONSORTIA_AVAILABLE_BALANCE_FLAG = 'consortia-available-balance';
export const CURRENT_FEATURE_FLAGS = [CONNECT_AUTHENTICATION_FLAG, CONSORTIA_AVAILABLE_BALANCE_FLAG];

function withProvider(WrappedComponent) {
  @autobind
  class FeatureFlagsProviderWrapper extends PureComponent<any, FeatureFlagsContextInterface> {
    state: FeatureFlagsContextInterface = {
      ...defaultValue,
      reload: this.loadFlags,
      get: this.getFlag,
      isEnabled: this.getFlag,
    };

    componentDidMount() {
      this.loadFlags();
    }

    getFlag(flag: string, defaultValue = false) {
      const entry = this.state.flags[flag];
      return entry ? !!entry.value : defaultValue;
    }

    @withErrorNotification
    @processing('isFetching')
    async loadFlags() {
      const flags = await fetchFeatureFlags();
      this.setState({ flags });
    }

    render() {
      return (
        <FeatureFlagsContext.Provider value={this.state}>
          <WrappedComponent {...this.props} />
        </FeatureFlagsContext.Provider>
      );
    }
  }

  return FeatureFlagsProviderWrapper;
}

export { FeatureFlagsContextInterface };
export default {
  useContext: () => React.useContext(FeatureFlagsContext),
  withProvider,
  withContext: mainWithContext(FeatureFlagsContext),
};
